/**
 * Builds a course-ready local accommodation list using FWS as the primary source
 * and SRM as fallback for missing student IRNs.
 *
 * Flow:
 * 1) Seed local state from FWS accommodations.
 * 2) Request person records for IRNs missing in FWS.
 * 3) Fetch SRM accommodations for those IRNs and merge them into local state.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getStudentApiAccommodations } from "../services/student/v1/student";
import { mapSrmDataToAcknowledgements } from "../utils/accommodationUtils";

export const useStudentAccommodationsWithSrmFallback = ({
  enabled = true,
  fwsStudentAccommodations,
  accommodatedStudentIrnListFromOsiris,
  personsWithNameMap,
  getPersonInfoWithName,
}) => {
  const [localStudentAccommodations, setLocalStudentAccommodations] = useState([]);
  const [fetchedFWSAccommodations, setFetchedFWSAccommodations] = useState(false);
  const [hasMissingFwsData, setHasMissingFwsData] = useState(false);
  const requestedSrmIrnsRef = useRef(new Set());

  const fetchSrmDataForMissingFwsAccommodations = useCallback(async ({
    accommodatedStudentIrnListFromOsiris,
    localStudentAccommodations,
    personsWithNameMap,
    requestedSrmIrnsRef,
    setLocalStudentAccommodations,
  }) => {
    const missingIrns = accommodatedStudentIrnListFromOsiris.filter(
      (irn) =>
        !localStudentAccommodations.some(
          (acc) => `${acc.studentIrn}` === `${irn}`
        )
    );

    if (!missingIrns.length) return;

    const allPersonsFetched = missingIrns.every(
      (irn) => !!personsWithNameMap?.[`${irn}-student`]
    );

    if (!allPersonsFetched) return;

    const irnsToFetch = missingIrns.filter((irn) => {
      if (requestedSrmIrnsRef.current.has(`${irn}`)) return false;
      requestedSrmIrnsRef.current.add(`${irn}`);
      return true;
    });

    if (!irnsToFetch.length) return;

    const fetched = await Promise.all(
      irnsToFetch.map(async (irn) => {
        try {
          const personId = personsWithNameMap?.[`${irn}-student`]?.id;
          if (!personId) return null;

          const srmResponse = await getStudentApiAccommodations(personId);
          const facultyAcknowledgements = mapSrmDataToAcknowledgements(
            srmResponse?.data
          );
          if (!facultyAcknowledgements.length) return null;

          return { studentIrn: `${irn}`, facultyAcknowledgements };
        } catch {
          return null;
        }
      })
    );

    const valid = fetched.filter(Boolean);
    if (!valid.length) return;

    setLocalStudentAccommodations((prev) => [
      ...prev,
      ...valid.filter(
        (newAcc) =>
          !prev.some((existing) => `${existing.studentIrn}` === `${newAcc.studentIrn}`)
      ),
    ]);
  }, []);

  // Reset hook state when disabled, or seed local accommodations from FWS data.
  useEffect(() => {
    if (!enabled) {
      setLocalStudentAccommodations([]);
      setFetchedFWSAccommodations(false);
      setHasMissingFwsData(false);
      requestedSrmIrnsRef.current = new Set();
      return;
    }

    if (
      Array.isArray(fwsStudentAccommodations) &&
      fwsStudentAccommodations.length > 0
    ) {
      setLocalStudentAccommodations(fwsStudentAccommodations);
      setFetchedFWSAccommodations(true);
    }
  }, [enabled, fwsStudentAccommodations]);

  // Request person records for IRNs that are still missing accommodation data from FWS.
  useEffect(() => {
    if (
      !enabled ||
      !fetchedFWSAccommodations ||
      !accommodatedStudentIrnListFromOsiris.length
    ) {
      return;
    }

    const missingIrns = accommodatedStudentIrnListFromOsiris.filter(
      (irn) =>
        !localStudentAccommodations.some(
          (accommodation) => `${accommodation.studentIrn}` === `${irn}`
        )
    );

    setHasMissingFwsData(missingIrns.length > 0);

    if (!missingIrns.length) {
      return;
    }

    missingIrns.forEach((irn) => {
      getPersonInfoWithName(irn, "student");
    });
  }, [
    enabled,
    fetchedFWSAccommodations,
    localStudentAccommodations.length,
    accommodatedStudentIrnListFromOsiris,
    getPersonInfoWithName,
  ]);

  // After person lookups resolve, fetch missing student accommodation data from SRM and merge it locally.
  useEffect(() => {
    if (
      !enabled ||
      !fetchedFWSAccommodations ||
      !accommodatedStudentIrnListFromOsiris.length
    ) {
      return;
    }

    fetchSrmDataForMissingFwsAccommodations({
      accommodatedStudentIrnListFromOsiris,
      localStudentAccommodations,
      personsWithNameMap,
      requestedSrmIrnsRef,
      setLocalStudentAccommodations,
    });
  }, [
    enabled,
    fetchedFWSAccommodations,
    localStudentAccommodations,
    accommodatedStudentIrnListFromOsiris,
    personsWithNameMap,
    fetchSrmDataForMissingFwsAccommodations,
  ]);

  return {
    hasMissingFwsData,
    localStudentAccommodations,
  };
};